import {DestroyRef, inject, Injectable, OnDestroy, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {
  combineLatest,
  concat,
  concatMap,
  EMPTY,
  first,
  from,
  map,
  mergeMap,
  NEVER,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
  toArray,
} from 'rxjs';
import {ACCOUNT_STEP} from 'src/app/pages/service/pages/account-page/enum/account-step.enum';
import {SALE_FORMAT_STEP} from 'src/app/pages/service/pages/account-page/enum/sale-format-step.enum';
import {ISaleFormatStep} from 'src/app/pages/service/pages/account-page/interface/sale-format-step.interface';

import * as uuid from 'uuid';
import {APP_ROUTE_PATH} from '../../enums';
import {CONSUMPTION_TYPE} from '../../enums/consumption-type.enum';

import {AccountStorePort} from 'src/app/core/entities/account/domain/ports/account-store.port';
import {ACCOUNT_STORE} from 'src/app/core/entities/account/domain/tokens/account-store.token';
import {AccountSelectedEvent} from '../../../shared/application/events/account/account-selected.event';
import {ACCOUNT_ROUTE_PATH} from '../../enums/routes/account-route-path.enum';
import {SERVICE_ROUTE_PATH} from '../../enums/routes/service-route-path.enum';
import {Logger} from '../../interfaces/Logger';
import {ISaleFormat} from '../../interfaces/sale-format.model';
import {TrackingService} from '../../services/tracking/tracking.service';
import {createAccountTracker} from '../../services/tracking/utils/account-tracker.util';
import {AccountModeService} from '../account/account-modes/account-mode.service';
import {ACCOUNT_MODE} from '../account/account-modes/enum/account-mode.enum';
import {AccountStepsService} from '../account/account-steps/account-steps.service';
import {OrganizationRepositoryAdapter} from '../organization/infrastructure/adapters/organization-repository.adapter';
import {ProductFormatService} from '../product-format/product-format.service';
import {Product} from '../product/interface/product.model';
import {ProductService} from '../product/product.service';
import {SaleFormatStepsService} from '../sale-format/sale-format-steps.service';
import {SaleFormatService} from '../sale-format/sale-format.service';
import {TicketEventService} from '../ticket-events/ticket-event.service';
import {ENTRY_TYPE} from './domain/enum/entry-type.enum';

import {TranslateService} from '@ngx-translate/core';
import {AllEntriesRemovedEvent} from 'src/app/shared/application/events/entry/all-entries-removed.event';
import {EVENT_BUS} from '../../../shared/domain/tokens/event-bus.token';
import {PAYMENT_STATUS} from '../../enums/payment-status.enum';
import {ENTRY_STATUS} from './domain/enum/entry-status.enum';
import {EntryDecreasedEvent} from './domain/events/entry-decreased.event';
import {EntryIncreasedEvent} from './domain/events/entry-increased.event';
import {Entry, IEntryConsumption} from './domain/model/entry.model';
import {EntryAuditHandlerService} from './entry-audit-handler.service';
import {IEntry, IEntryDiscount,} from './infrastructure/interface/entry-storage.interface';
import {EntryMapper} from './infrastructure/mapper/entry.mapper';
import {EntriesChangedEvent} from '../../../shared/application/events/entry/entries-changed.event';

@Injectable({
  providedIn: 'root',
})
export class EntryService implements OnDestroy {
  private readonly accountStepsService = inject(AccountStepsService);
  private readonly organizationService = inject(OrganizationRepositoryAdapter);
  private readonly entryAuditHandler = inject(EntryAuditHandlerService);
  private readonly router = inject(Router);
  private readonly logger = inject(Logger);
  private readonly productService = inject(ProductService);
  private readonly saleFormatService = inject(SaleFormatService);
  private readonly productFormatService = inject(ProductFormatService);
  private readonly saleFormatStepsService = inject(SaleFormatStepsService);
  private readonly ticketEventService = inject(TicketEventService);
  private readonly accountModeService = inject(AccountModeService);
  private readonly trackingService = inject(TrackingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly eventBus = inject(EVENT_BUS);
  private readonly trackAccount = createAccountTracker(this.trackingService);
  private readonly accountStoreService =
    inject<AccountStorePort>(ACCOUNT_STORE);
  private readonly translateService = inject(TranslateService);

  private _selectedEntry!: Entry | null;
  get selectedEntry(): Entry | null {
    return this._selectedEntry;
  }

  set selectedEntry(value: Entry | null) {
    this._selectedEntry = value;
  }

  readonly _entryLimit = signal<number>(0);

  get entryLimit(): number {
    return this._entryLimit();
  }

  set entryLimit(value: number) {
    this._entryLimit.set(value);
  }

  /**
   * Navigates to the entry discount view.
   * @returns {void}
   */
  openEntryDiscountView(): void {
    this.router.navigate(
      [
        `${APP_ROUTE_PATH.service}/${SERVICE_ROUTE_PATH.account}/${ACCOUNT_ROUTE_PATH.discount}`,
      ],
      {replaceUrl: true}
    );
  }

  /**
   * Prepares the view for adding addons to the selected entry, based on product and sale format.
   * @returns {void}
   */
  prepareEntryAddonsView(): void {
    const entry = this.selectedEntry;

    if (!entry?.product_id) {
      return;
    }

    const {
      product_id: productId,
      sale_format_id: saleFormatId,
      product_format_id: productFormatId,
    } = entry;

    if (saleFormatId) {
      this.prepareSaleFormatAddons(productId, saleFormatId);

      return;
    }

    if (productFormatId) {
      this.productFormatService
        .getProductFormatById(productFormatId)
        .pipe(first(), takeUntilDestroyed(this.destroyRef))
        .subscribe((productFormat) =>
          this.decideAddonsSource(productId, productFormat)
        );

      return;
    }

    this.prepareProductsAddons(productId);
  }

  private decideAddonsSource(
    productId: string,
    productFormat:
      | { sale_format?: { _id?: string; max_addons?: number } }
      | null
      | undefined
  ): void {
    const saleFormatId = productFormat?.sale_format?._id ?? '';
    const maxAddons = productFormat?.sale_format?.max_addons ?? 0;

    if (saleFormatId && maxAddons > 0) {
      this.prepareSaleFormatAddons(productId, saleFormatId);
    } else {
      this.prepareProductsAddons(productId);
    }
  }

  /**
   * Prepares the addons for a product with a specific sale format.
   * @param {string} productId - The product ID.
   * @param {string} saleFormatId - The sale format ID.
   * @returns {void}
   */
  prepareSaleFormatAddons(productId: string, saleFormatId: string): void {
    this.productService
      .getProductByPartialId(productId)
      .pipe(
        first(),
        switchMap((product) => {
          return this.saleFormatService.getSaleFormatById(saleFormatId).pipe(
            first(),
            map((saleFormat) => ({product, saleFormat}))
          );
        }),
        switchMap(({product, saleFormat}) => {
          if (!saleFormat) {
            this.logger.error(
              new Error(`Error opening addons view, sale format is missing`)
            );

            return of({product, finalSaleFormat: null});
          }

          return this.saleFormatService
            .insertCurrentSaleFormat(saleFormat)
            .pipe(
              switchMap(() => {
                if (
                  this.selectedEntry?.product_id ===
                  this.selectedEntry?.product_format_id
                ) {
                  return this.productFormatService.getProductFormatById(
                    productId
                  );
                } else {
                  return this.productFormatService.getProductFormatByProductIdAndFormatId(
                    productId,
                    saleFormat._id
                  );
                }
              }),
              switchMap((productFormat) =>
                this.productFormatService.insertCurrentProductFormat(
                  productFormat
                )
              ),
              switchMap(() => {
                if (product) {
                  return this.productService.insertCurrentProduct(product);
                }

                return of(null);
              }),
              map(() => ({product, finalSaleFormat: saleFormat}))
            );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({finalSaleFormat}) => {
          if (finalSaleFormat?.max_addons && finalSaleFormat.max_addons > 0) {
            const step: ISaleFormatStep = {
              step: SALE_FORMAT_STEP.addAddons,
              productId: productId,
              saleFormatId: finalSaleFormat._id,
              productName: this.selectedEntry?.product_name,
              hasSaleFormatShortcut: true,
            };

            this.goToSaleFormatsView(step);
          }
        },
        error: (error: Error) => {
          this.logger.error(
            new Error(
              '[EntryService] [prepareEntryAddonsView] Error preparing entry addons view'
            ),
            error
          );
        },
      });
  }

  /**
   * Prepares the addons for a product without a sale format.
   * @param {string} productId - The product ID.
   * @returns {void}
   */
  prepareProductsAddons(productId: string): void {
    this.productService
      .getProductByPartialId(productId)
      .pipe(
        first(),
        mergeMap((currentProduct: Product | null) => {
          return this.productService.insertCurrentProduct(currentProduct!);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((product) => {
        const step: ISaleFormatStep = {
          step: SALE_FORMAT_STEP.addProductAddons,
          productId: product?._id,
          productName: product?.name,
        };

        this.goToSaleFormatsView(step);
      });
  }

  /**
   * Navigates to the sale formats view with the provided step.
   * @param {ISaleFormatStep} step - The sale format step to navigate to.
   * @returns {void}
   */
  goToSaleFormatsView(step: ISaleFormatStep): void {
    this.saleFormatStepsService.currentStep = step;
    this.accountStepsService.setCurrentStep({step: ACCOUNT_STEP.saleFormats});

    if (
      this.saleFormatStepsService.currentStep.step === SALE_FORMAT_STEP.initial
    ) {
      this.saleFormatStepsService.reloadAction$.next();
    } else {
      this.router.navigate(
        [
          `${APP_ROUTE_PATH.service}/${SERVICE_ROUTE_PATH.account}/${ACCOUNT_ROUTE_PATH.formats}`,
        ],
        {replaceUrl: true}
      );
    }
  }

  /**
   * Unselects all entries in the current account.
   * @returns {Observable<Account | null>} Observable emitting the updated account.
   */
  unselectEntries(): Observable<void> {
    const currentAccount = this.accountStoreService.getSelectedAccount();

    if (!currentAccount) {
      return EMPTY;
    }

    currentAccount.entries?.map((accountEntry: Entry) => {
      accountEntry?.updateSelected(false);
    });

    return combineLatest({
      saveSelectedAccount:
        this.accountStoreService.saveSelectedAccount(currentAccount),
      saveAccounts: this.accountStoreService.saveAccounts([currentAccount]),
    }).pipe(
      first(),
      map(() => void 0)
    );
  }

  /**
   * Selects a specific entry and unselects others.
   * @param {Entry} entry - The entry to select.
   * @returns {Observable<Entry>} Observable emitting the selected entry.
   */
  selectEntry(entry: Entry): Observable<Entry> {
    const currentAccount = this.accountStoreService.getSelectedAccount();

    if (!currentAccount) {
      return EMPTY;
    }

    return of(currentAccount).pipe(
      mergeMap((currentAccount) => {
        if (!currentAccount) {
          return EMPTY;
        }

        const selectedEntries = currentAccount.entries?.filter(
          (accountEntry: Entry) => accountEntry.isSelected
        );

        const unselect$ = selectedEntries?.map((selectedEntry: Entry) => {
          const entry = EntryMapper.deepClone(selectedEntry);

          entry.updateSelected(false);

          return this.updateEntryOnCurrentAccount(entry, false);
        });

        if (unselect$?.length) {
          return concat([...unselect$]);
        }

        return concat([of(undefined)]);
      }),
      mergeMap(() => {
        entry = EntryMapper.deepClone(entry);

        entry.updateSelected(true);

        this._selectedEntry = entry;

        return this.updateEntryOnCurrentAccount(entry, false);
      })
    );
  }

  /**
   * Updates the addons of the given entry based on the provided product.
   * @param {Entry} entry - The entry to update.
   * @param {Product} product - The product containing the new addons.
   * @returns {Observable<Entry>} Observable emitting the updated entry.
   */
  updateEntryAddons(entry: Entry, product: Product): Observable<Entry> {
    entry = EntryMapper.deepClone(entry);

    const formattedAddons =
      product.addons?.map((addon) => {
        return {
          addon: {
            _id: addon._id,
            product_id: addon.product_addon_id,
            name: addon.addon_product.name,
            color: addon.addon_product.color,
            amount: addon.amount ?? 0,
          },
          quantity: addon.quantity ?? 0,
          initial_quantity: addon.quantity ?? 0,
          total: (addon.quantity ?? 0) * (addon.amount ?? 0),
        };
      }) ?? [];

    entry.updateSelectedAddons(formattedAddons);
    entry.updateAddonsPrice();
    entry.recalculateTotal();

    return this.updateEntryOnCurrentAccount(entry);
  }

  /**
   * Adds a new entry to the current account and emits a socket event.
   * @param {Product} product - The product to add as an entry.
   * @param {ISaleFormat} [saleFormat] - Optional sale format for the entry.
   * @returns {Observable<[Account, Entry]>} Observable emitting the updated account and the new entry.
   */
  addEntryToAccount(
    product: Product,
    saleFormat?: ISaleFormat
  ): Observable<Entry> {
    const ticketEvent$ = this.accountModeService
      .getAccountMode()
      .pipe(
        switchMap((mode) =>
          mode === ACCOUNT_MODE.qr_complement
            ? this.ticketEventService.getCurrentTicketEvent()
            : of(null)
        )
      );

    return combineLatest({
      organization: this.organizationService.getCurrentOrganization(),
      accountMode: this.accountModeService.getAccountMode(),
      currentTicketEvent: ticketEvent$,
    }).pipe(
      first(),
      switchMap(({organization, accountMode, currentTicketEvent}) => {
        const currentAccount = this.accountStoreService.getSelectedAccount();

        if (!currentAccount) {
          const error = new Error('Error fetching current account');

          this.logger.error(error);

          return throwError(() => error);
        }

        if (!organization) {
          const error = new Error('Error fetching current organization');

          this.logger.error(error);

          return throwError(() => error);
        }

        let consumption: IEntryConsumption | undefined = undefined;

        if (accountMode === ACCOUNT_MODE.qr_complement && currentTicketEvent) {
          consumption = {type: CONSUMPTION_TYPE.complement};
        }

        const entryInterface: IEntry = {
          _id: uuid.v4(),
          account_id: currentAccount?.id,
          negocio_id: organization._id,
          associated_id: product._id,
          associated_name: product.name,
          product_name:
            (saleFormat && !saleFormat.addons ? saleFormat.name + ' ' : '') +
            product.name,
          product_id: product._id,
          activated: product.activated,
          quantity: product.quantity,
          quantity_to_display: product.quantity,
          unit_price: product.price.amount,
          color: product.color,
          taxes_ids: product.taxes_ids,
          isSelected: false,
          created_at: Date.now(),
          closed_at: 0,
          vat: product.taxes[0]?.percentage,
          currency: organization.currency,
          consumption,
          type: product.type as unknown as ENTRY_TYPE,
          limit: product.limit,
          initial_unit_price: product.price.amount,
          total: product.price.amount,
          initial_total: product.price.amount,
          selected_addons: [],
          unit_price_modified: product.price.amount,
          refund_id: undefined,
          addons_total: 0,
          max_addons: 0,
          product_format_id: undefined,
          sale_format_id: undefined,
          sale_format_name: undefined,
          status: ENTRY_STATUS.NOT_CHARGED,
        };

        if (!product.hasFormatsEnabled && product.max_addons) {
          entryInterface.max_addons = product.max_addons!;
        }

        if (saleFormat) {
          entryInterface.sale_format_id = saleFormat._id;
          entryInterface.sale_format_name = saleFormat.name;
          entryInterface.max_addons = saleFormat.max_addons!;
          entryInterface.product_format_id =
            this.productFormatService.getCurrentProductFormatFromCache()?._id;
        }

        const entry = EntryMapper.toModel(entryInterface);

        if (product.addons) {
          entry.setAddons(product.addons);
        }

        entry.recalculateTotal();

        this.entryAuditHandler
          .auditAddNewEntryToAccountEvent(entry)
          .pipe(first(), takeUntilDestroyed(this.destroyRef))
          .subscribe();

        this.trackAccount('Entry created', {id: entry._id});

        currentAccount.addEntry(entry);
        this.eventBus.publish(new EntriesChangedEvent([entry], 'added'));

        return combineLatest({
          saveSelectedAccount:
            this.accountStoreService.saveSelectedAccount(currentAccount),
          saveAccounts: this.accountStoreService.saveAccounts([currentAccount]),
        }).pipe(
          first(),
          tap(() =>
            this.eventBus.publish(new AccountSelectedEvent(currentAccount))
          ),

          switchMap(() => {
            return this.unifyEntriesWithoutDiscountByProduct().pipe(
              map((unifiedEntry) => unifiedEntry || entry)
            );
          })
        );
      }),
      tap((entry) => this.eventBus.publish(new EntryIncreasedEvent(entry)))
    );
  }

  isEntryInAccountPayments(entry: Entry): boolean {
    const currentAccount = this.accountStoreService.getSelectedAccount();

    if (!currentAccount) {
      return false;
    }

    if (!currentAccount?.payments?.length) {
      return false;
    }

    const completedPayments = currentAccount.payments.filter(
      (payment) => payment.status === PAYMENT_STATUS.completed
    );

    for (const payment of completedPayments) {
      const entryInPayment = payment.entries?.find(
        (entryInPayment: Entry) => entry._id === entryInPayment._id
      );

      if (entryInPayment) {
        return true;
      }
    }

    return false;
  }

  /**
   * Gets the total quantity of a specific entry across all payments in the current account.
   * @param {Entry} entry - The entry to check.
   * @returns {number} The total quantity of the entry found in all payments.
   */
  getTotalQuantityInPayments(entry: Entry): number {
    const currentAccount = this.accountStoreService.getSelectedAccount();

    if (!currentAccount?.payments?.length) {
      return 0;
    }

    let totalQuantityInPayments = 0;

    const completedPayments = currentAccount.payments.filter(
      (payment) => payment.status === PAYMENT_STATUS.completed
    );

    for (const payment of completedPayments) {
      const entryInPayment = payment.renderedEntries?.find(
        (entryInPayment: Entry) => entry._id === entryInPayment._id
      );

      if (entryInPayment) {
        totalQuantityInPayments += entryInPayment.quantityToDisplay;
      }
    }

    return totalQuantityInPayments;
  }

  /**
   * Increases the quantity of a specific entry in the current account by one.
   * @param {Entry} entry - The entry to update.
   * @returns {Observable<Account | null>} Observable emitting the updated account.
   */
  increaseEntryUnit(entry: Entry): Observable<Entry> {
    const currentAccount = this.accountStoreService.getSelectedAccount();

    if (!currentAccount) {
      return EMPTY;
    }

    return of(currentAccount).pipe(
      first(),
      switchMap((currentAccount) => {
        if (!currentAccount) {
          this.logger.error(new Error('Error fetching current account'));

          return EMPTY;
        }

        this.entryAuditHandler
          .auditUpdateEntryUnitEvent(entry)
          .pipe(first(), takeUntilDestroyed(this.destroyRef))
          .subscribe();

        entry.addEntryUnit();

        if (entry.discount) {
          entry.discount.initial_quantity = entry.quantityToDisplay;
        }

        const updatedEntry = EntryMapper.deepClone(entry);

        currentAccount.updateEntry(updatedEntry);
        this.eventBus.publish(
          new EntriesChangedEvent([updatedEntry], 'updated')
        );

        return combineLatest({
          saveSelectedAccount:
            this.accountStoreService.saveSelectedAccount(currentAccount),
          saveAccounts: this.accountStoreService.saveAccounts([currentAccount]),
        }).pipe(
          tap(() => this.eventBus.publish(new EntryIncreasedEvent(entry))),
          map(() => entry)
        );
      })
    );
  }

  /**
   * Decreases the quantity of a specific entry in the current account by one.
   * @param {Entry} entry - The entry to update.
   * @returns {Observable<Account | null>} Observable emitting the updated account.
   */
  decreaseEntryUnit(entry: Entry): Observable<Entry> {
    const currentAccount = this.accountStoreService.getSelectedAccount();

    if (!currentAccount) {
      return EMPTY;
    }

    return of(currentAccount).pipe(
      first(),
      switchMap((currentAccount) => {
        if (!currentAccount) {
          this.logger.error(new Error('Error fetching current account'));

          return EMPTY;
        }

        this.entryAuditHandler
          .auditUpdateEntryUnitEvent(entry)
          .pipe(first(), takeUntilDestroyed(this.destroyRef))
          .subscribe();

        entry.removeEntryUnit();

        if (entry.discount) {
          if (entry.total <= 0) {
            entry.removeDiscount();
          } else {
            entry.discount.initial_quantity = entry.quantityToDisplay;
          }
        }

        const updatedEntry = EntryMapper.deepClone(entry);

        currentAccount.updateEntry(updatedEntry);
        this.eventBus.publish(
          new EntriesChangedEvent([updatedEntry], 'updated')
        );

        if (entry.quantityToDisplay === 0) {
          return this.removeEntryFromAccount(entry);
        }

        return combineLatest({
          saveSelectedAccount:
            this.accountStoreService.saveSelectedAccount(currentAccount),
          saveAccounts: this.accountStoreService.saveAccounts([currentAccount]),
        }).pipe(
          tap(() =>
            this.eventBus.publish(new AccountSelectedEvent(currentAccount))
          ),

          tap(() => this.eventBus.publish(new EntryDecreasedEvent(entry))),
          map(() => entry)
        );
      })
    );
  }

  /**
   * Updates an entry on the current account and optionally emits a socket event.
   * @param {Entry} entry - The entry to update.
   * @param {boolean} [sendUpdateSocketEvent=true] - Whether to emit a socket event for the update.
   * @returns {Observable<Entry>} Observable emitting the updated entry.
   */
  updateEntryOnCurrentAccount(
    entry: Entry,
    sendUpdateSocketEvent = true
  ): Observable<Entry> {
    const currentAccount = this.accountStoreService.getSelectedAccount();

    if (!currentAccount) {
      return EMPTY;
    }

    return of(currentAccount).pipe(
      first(),
      switchMap((currentAccount) => {
        if (!currentAccount) {
          return EMPTY;
        }

        const hasEntry = currentAccount.entries?.find(
          (entryToLook: Entry) => entryToLook?._id === entry?._id
        );

        if (!hasEntry) {
          return NEVER;
        }

        if (sendUpdateSocketEvent) {
          this.entryAuditHandler
            .auditUpdateEntryUnitEvent(entry)
            .pipe(first(), takeUntilDestroyed(this.destroyRef))
            .subscribe();
        }

        currentAccount.updateEntry(entry);
        this.eventBus.publish(new EntriesChangedEvent([entry], 'updated'));

        if (entry.quantityToDisplay === 0) {
          return this.removeEntryFromAccount(entry).pipe(
            switchMap(() => of(entry))
          );
        }

        return combineLatest({
          saveSelectedAccount:
            this.accountStoreService.saveSelectedAccount(currentAccount),
          saveAccounts: this.accountStoreService.saveAccounts([currentAccount]),
        }).pipe(map(() => entry));
      })
    );
  }

  /**
   * Removes an entry from the current account.
   * @param {Entry} entry - The entry to remove.
   * @returns {Observable<Account | null>} Observable emitting the updated account.
   */
  removeEntryFromAccount(entry: Entry): Observable<Entry> {
    const currentAccount = this.accountStoreService.getSelectedAccount();

    if (!currentAccount) {
      return EMPTY;
    }

    return of(currentAccount).pipe(
      first(),
      switchMap((currentAccount) => {
        if (!currentAccount) {
          this.logger.error(new Error('Error fetching current account'));

          return EMPTY;
        }

        this.entryAuditHandler
          .auditRemoveEntryUnitEvent(entry)
          .pipe(first(), takeUntilDestroyed(this.destroyRef))
          .subscribe();

        currentAccount.removeEntry(entry);
        this.eventBus.publish(new EntriesChangedEvent([entry], 'removed'));

        return combineLatest({
          saveSelectedAccount:
            this.accountStoreService.saveSelectedAccount(currentAccount),
          saveAccounts: this.accountStoreService.saveAccounts([currentAccount]),
        }).pipe(
          tap(() =>
            this.eventBus.publish(new AccountSelectedEvent(currentAccount))
          ),

          tap(() => this.eventBus.publish(new EntryDecreasedEvent(entry))),
          map(() => entry)
        );
      })
    );
  }

  /**
   * Removes all entries from the current account that are not in payments and are removable.
   * Only processes entries that are NOT in 'pending' or 'charged' status.
   * For entries that are partially paid, reduces them to only the paid quantity.
   * @returns {Observable<void>} Observable emitting the updated account.
   */
  removeAllEntriesFromAccount(): Observable<void> {
    const currentAccount = this.accountStoreService.getSelectedAccount();

    if (!currentAccount) {
      return EMPTY;
    }

    return of(currentAccount).pipe(
      first(),
      switchMap((currentAccount) => {
        if (!currentAccount) {
          this.logger.error(new Error('Error fetching current account'));

          return EMPTY;
        }

        const entries = currentAccount.entries || [];
        const entriesToRemove: Entry[] = [];
        const entriesToUpdate: Entry[] = [];

        entries.forEach((entry: Entry) => {
          const isRemovableEntry =
            !entry.status || entry.status === ENTRY_STATUS.NOT_CHARGED;

          if (!isRemovableEntry) {
            return;
          }

          const paidQuantity = this.getTotalQuantityInPayments(entry);

          if (paidQuantity === 0) {
            entriesToRemove.push(entry);
          } else if (entry.quantityToDisplay > paidQuantity) {
            const entryToUpdate = EntryMapper.deepClone(entry);

            entryToUpdate.changeEntryQuantity(paidQuantity);
            entriesToUpdate.push(entryToUpdate);
          }
        });

        const removeOperations$ = entriesToRemove.map((entry: Entry) =>
          this.entryAuditHandler.auditRemoveEntryUnitEvent(entry).pipe(
            first(),
            takeUntilDestroyed(this.destroyRef),
            tap(() => {
              currentAccount.removeEntry(entry);
              this.eventBus.publish(
                new EntriesChangedEvent([entry], 'removed')
              );
            })
          )
        );

        const updateOperations$ = entriesToUpdate.map((entry: Entry) =>
          this.entryAuditHandler.auditUpdateEntryUnitEvent(entry).pipe(
            first(),
            takeUntilDestroyed(this.destroyRef),
            tap(() => {
              currentAccount.updateEntry(entry);
              this.eventBus.publish(
                new EntriesChangedEvent([entry], 'updated')
              );
            })
          )
        );

        const allOperations$ = [...removeOperations$, ...updateOperations$];

        if (allOperations$.length === 0) {
          return of(void 0);
        }

        return from(allOperations$).pipe(
          concatMap((operation$) => operation$),
          toArray(),
          switchMap(() => {
            return combineLatest({
              saveSelectedAccount:
                this.accountStoreService.saveSelectedAccount(currentAccount),
              saveAccounts: this.accountStoreService.saveAccounts([
                currentAccount,
              ]),
            }).pipe(
              tap(() => {
                if (entriesToRemove.length > 0) {
                  const entriesId = entriesToRemove.map((entry) => entry._id);

                  this.eventBus.publish(
                    new AllEntriesRemovedEvent({entriesId})
                  );
                }
              }),
              map(() => void 0)
            );
          })
        );
      })
    );
  }

  /**
   * Adds a discount to a specific entry.
   * @param {Entry} entry - The entry to update.
   * @param {IEntryDiscount} discount - The discount to apply.
   * @returns {Observable<Entry>} Observable emitting the updated entry.
   */
  addDiscountToEntry(
    entry: Entry,
    discount: IEntryDiscount
  ): Observable<Entry> {
    entry.addDiscount(discount);

    return this.updateEntryOnCurrentAccount(entry).pipe(
      switchMap((updatedEntry) => {
        return this.unifyEntriesWithSameDiscountByProduct().pipe(
          map((unifiedEntry) => unifiedEntry || updatedEntry)
        );
      })
    );
  }

  /**
   * Removes the discount from a specific entry.
   * @param {Entry} entry - The entry to update.
   * @returns {Observable<Entry>} Observable emitting the updated entry.
   */
  removeDiscountToEntry(entry: Entry): Observable<Entry> {
    entry.removeDiscount();

    return this.updateEntryOnCurrentAccount(entry);
  }

  /**
   * Unifies entries without discounts by product in the current account.
   */
  private getEntryKey(entry: Entry): string {
    const saleFormatId = entry.sale_format_id || '';

    const addons = (entry.selectedAddons || [])
      .map((addon) => `${addon.addon._id}:${addon.quantity}`)
      .sort()
      .join('|');

    return `${entry.product_id}__${saleFormatId}__${addons}`;
  }

  unifyEntriesWithoutDiscountByProduct(): Observable<Entry | undefined> {
    const allEntries =
      this.accountStoreService.getSelectedAccount()?.entries || [];

    this.logger.debug(
      `🔄 [EntryService] unifyEntriesWithoutDiscountByProduct: Processing ${allEntries.length} total entries`
    );

    allEntries.forEach((entry, index) => {
      this.logger.debug(
        `  Entry ${index + 1}: id=${entry._id}, product=${
          entry.product_name
        }, hasDiscount=${entry.hasDiscountApplied}, isCharged=${
          entry.isCharged
        }, isPending=${entry.isPending}`
      );
    });

    const entries = allEntries.filter(
      (entry) =>
        !entry.hasDiscountApplied && !entry.isCharged && !entry.isPending
    );

    this.logger.debug(
      `✅ [EntryService] Filtered to ${entries.length} UNIFIABLE entries (no discount, not charged, not pending)`
    );

    const groups = new Map<string, Entry[]>();

    entries.forEach((entry) => {
      const key = this.getEntryKey(entry);

      if (!groups.has(key)) {
        groups.set(key, []);
      }

      groups.get(key)!.push(entry);
    });

    for (const groupEntries of groups.values()) {
      if (groupEntries.length > 1) {
        this.logger.debug(
          `🔗 [EntryService] Found ${groupEntries.length} entries to unify:`
        );
        groupEntries.forEach((entry, index) => {
          this.logger.debug(
            `  Unify Entry ${index + 1}: id=${entry._id}, quantity=${
              entry.quantityToDisplay
            }`
          );
        });

        const totalQuantity = groupEntries.reduce(
          (accumulator, entry) => accumulator + entry.quantityToDisplay,
          0
        );
        const [firstEntry, ...duplicates] = groupEntries;

        this.logger.debug(
          `🎯 [EntryService] Unifying into single entry: ${firstEntry._id} with total quantity=${totalQuantity}`
        );

        const removeOps$ = duplicates.map((entry) =>
          this.removeEntryFromAccount(entry)
        );

        return concat(...removeOps$).pipe(
          switchMap(() => {
            const unified = EntryMapper.deepClone(firstEntry);

            unified.updateQuantity(totalQuantity);
            unified.updateQuantityToDisplay(totalQuantity);

            unified.recalculateTotal();

            this.logger.debug(
              `✅ [EntryService] Unified entry created with quantity=${totalQuantity}`
            );

            return this.updateEntryOnCurrentAccount(unified, false);
          })
        );
      }
    }

    this.logger.debug(
      `🚫 [EntryService] No entries to unify - returning undefined`
    );

    return of(undefined);
  }

  /**
   * Unifies entries with the same discount by product in the current account.
   * @returns {Observable<Entry | undefined>} Observable emitting the unified entry or undefined.
   */
  /**
   * Unifies entries with the same discount by product in the current account.
   */
  unifyEntriesWithSameDiscountByProduct(): Observable<Entry | undefined> {
    const entries = this.accountStoreService.getSelectedAccount()
            ?.entries?.filter((entry) => entry.hasDiscountApplied) || [];

    const groups = new Map<string, Entry[]>();

    entries.forEach((entry) => {
      if (entry.product_id) {
        let key = `${entry.product_id}:${
          entry.hasUnitPriceModified ? entry.unitPriceModified : 'discount'
        }`;

        key += `:${entry.discount?.percentage_discount || 0} _percentage_discount`;
        key += `:${entry.discount?.price_discount || 0} _price_discount`;
        key += `:${entry.discount?.splitted || false} _splitted`;

        if(entry.sale_format_id){
          key += `:${entry.sale_format_id}`;
        }

        if(entry.selectedAddons && entry.selectedAddons.length > 0){
          const sortedAddons = [...entry.selectedAddons].sort((a, b) =>
            a.addon._id.localeCompare(b.addon._id)
          );

          key += `:${sortedAddons.map((addon) => `${addon.addon._id}:${addon.quantity}`).join('|')}`;
        }

        if (!groups.has(key)) {
          groups.set(key, []);
        }

        groups.get(key)!.push(entry);
      }
    });

    for (const groupEntries of groups.values()) {
      if (groupEntries.length > 1) {
        const totalQuantity = groupEntries.reduce(
          (accumulator, entry) => accumulator + entry.quantityToDisplay,
          0
        );
        const [firstEntry, ...duplicates] = groupEntries;

        const removeOps$ = duplicates.map((entry) =>
          this.removeEntryFromAccount(entry)
        );

        return concat(...removeOps$).pipe(
          switchMap(() => {
            const unified = EntryMapper.deepClone(firstEntry);

            unified.updateQuantity(totalQuantity);
            unified.updateQuantityToDisplay(totalQuantity);

            if (unified.discount) {
              unified.discount.initial_quantity = totalQuantity;
            }

            unified.recalculateTotal();

            return this.updateEntryOnCurrentAccount(unified, false);
          })
        );
      }
    }

    return of(undefined);
  }

  /**
   * Cleanup resources when service is destroyed
   * Complete all Subjects to prevent memory leaks
   */
  ngOnDestroy(): void {
    this.logger.debug('[EntryService] Cleaning up resources');
  }
}
